import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { useRecordBenefitVerdictMutation } from "store/queries/benefitComparison";
import {
  ComparisonBenefit,
  IBenefitVerdict,
  IRecordVerdictRequest,
  IVerdictOption,
} from "types/BenefitComparison/BenefitComparison";

import {
  AGREES_WITH_LABEL,
  NEUTRAL_TAG_CLASS,
  ANOTHER_VALUE_LABEL,
  CANNOT_DETERMINE_LABEL,
  COMPARISON_BENEFIT_LABELS,
  formatComparisonValue,
  RECORD_VERDICT_LABEL,
  SIDE_LABELS,
  verdictByline,
  SIDE_TAGS,
  SOURCE_TAGS,
  verdictConsequence,
  verdictQuestion,
  verdictOptionSources,
  verdictOptionValueLabel,
  VERDICT_SOURCE_LABELS,
  VERDICT_SOURCE_TAGS,
} from "./benefitComparisonWording";

/** What the radio group can be on: one of the offered values, a typed one, or "can't tell". */
type Choice =
  | { kind: "OPTION"; index: number }
  | { kind: "OTHER" }
  | { kind: "CANNOT_DETERMINE" };

/**
 * CLE-2820 — recording ONE verdict: what the estimate should have quoted for this check × benefit.
 *
 * 🚨 THE REVIEWER RECORDS A VALUE, NOT A WINNER. The options are the DISTINCT values, labelled with
 * whoever happens to have picked each, and the value is what is stored. There is no "Legacy was
 * right" button and there never will be: that is what keeps a verdict true after the engine's
 * configuration changes, and it is what lets CLE-2821 re-score it under next month's config.
 *
 * 🚨 SELECTING IS NOT SUBMITTING. The options are a RADIO GROUP — choosing one writes nothing, and
 * the panel says in one sentence what recording will do. Only "Record human verdict" writes; Cancel
 * discards. A recorded verdict can be changed afterwards, and doing so RE-SEEDS the check rather
 * than counting as a regression: correcting yourself must never look like the engine breaking.
 *
 * 🚨 "CAN'T TELL FROM THIS RESPONSE" IS A REAL ANSWER, not an escape hatch, and it sits in the same
 * group as the values — a response that does not answer the question is a finding about the response.
 *
 * 🚨 NO BULK VERDICT, and NO KEYBOARD SHORTCUTS. One check at a time by click: a bulk mistake spreads
 * across a whole cluster, and there is no digit-key or J/K binding anywhere in this design.
 */
export function BenefitVerdictPanel({
  pverifyResponseId,
  benefit,
  options,
  verdict,
  remitValue,
  remitConflict,
  agreement,
  compact = false,
}: {
  pverifyResponseId: string;
  benefit: ComparisonBenefit;
  options: IVerdictOption[];
  verdict: IBenefitVerdict | null;
  remitValue?: number | null;
  remitConflict?: boolean;
  /** Which sides agree with the verdict NOW. Derived by the API at display time, stored nowhere. */
  agreement?: Record<"legacy" | "engine" | "ai", boolean | null> | null;
  compact?: boolean;
}) {
  const [record, { isLoading, isError }] = useRecordBenefitVerdictMutation();
  const [choice, setChoice] = useState<Choice | null>(null);
  const [other, setOther] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [changing, setChanging] = useState(false);

  const reset = () => {
    setChanging(false);
    setChoice(null);
    setOther(null);
    setNote("");
  };

  const save = async (body: IRecordVerdictRequest) => {
    await record(body);
    reset();
  };

  const showForm = !verdict || changing;

  const chosenLabel = (() => {
    if (choice === null) return null;
    if (choice.kind === "CANNOT_DETERMINE") return CANNOT_DETERMINE_LABEL;
    if (choice.kind === "OTHER")
      return other === null
        ? null
        : String(formatComparisonValue(other, benefit));
    return verdictOptionValueLabel(options[choice.index], benefit);
  })();

  const submit = () => {
    if (choice === null) return;
    const base = {
      pverifyResponseId,
      benefit,
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    if (choice.kind === "CANNOT_DETERMINE") {
      save({ ...base, verdict: "CANNOT_DETERMINE" });
      return;
    }
    const value =
      choice.kind === "OTHER" ? other : options[choice.index]?.value ?? null;
    if (value === null) return;
    save({ ...base, verdict: "VALUE", value });
  };

  const isChosen = (option: IVerdictOption) =>
    verdict?.verdict === "VALUE" &&
    verdict.value !== null &&
    Math.abs(verdict.value - option.value) < 0.005;

  return (
    <div
      className="flex flex-column gap-2"
      data-testid={`benefitVerdict.${pverifyResponseId}.${benefit}`}
    >
      {!compact && (
        <span className="font-semibold">
          {COMPARISON_BENEFIT_LABELS[benefit]}
        </span>
      )}
      <span className="text-color-secondary">{verdictQuestion(benefit)}</span>

      {verdict && (
        <div
          className="flex flex-wrap align-items-center gap-2"
          data-testid="benefitVerdict.current"
        >
          <Tag
            {...(verdict.verdict === "VALUE"
              ? SOURCE_TAGS.human
              : { className: NEUTRAL_TAG_CLASS })}
            value={
              verdict.verdict === "VALUE"
                ? `Human input ${formatComparisonValue(verdict.value, benefit)}`
                : `Human input: ${CANNOT_DETERMINE_LABEL.toLowerCase()}`
            }
          />
          <span className="text-sm text-color-secondary">
            {verdictByline(verdict)}
          </span>
          {!changing && (
            <Button
              type="button"
              label="Change"
              className="p-button-text p-button-sm"
              data-testid="benefitVerdict.change"
              onClick={() => setChanging(true)}
            />
          )}
        </div>
      )}

      {/* The reason, in the reviewer's own words — what tells the next reader why the verdict says this. */}
      {verdict?.note && !changing && (
        <span
          className="text-sm font-italic"
          data-testid="benefitVerdict.currentNote"
        >
          “{verdict.note}”
        </span>
      )}

      {/* 🚨 Which sides agree with the verdict NOW. Derived at display time; the verdict is a VALUE. */}
      {verdict && !changing && agreement && (
        <div
          className="flex flex-wrap align-items-center gap-2 text-sm"
          data-testid="benefitVerdict.agreement"
        >
          <span>{AGREES_WITH_LABEL}:</span>
          {(["legacy", "engine", "ai"] as const).map((side) =>
            agreement[side] === null ? null : (
              <Tag
                key={side}
                {...SIDE_TAGS[side]}
                icon={agreement[side] ? "pi pi-check" : "pi pi-times"}
                value={SIDE_LABELS[side]}
              />
            )
          )}
        </div>
      )}

      {/* 🚨 TRUTH ORDER: a remit OUTRANKS a verdict. Both are shown; neither is overwritten or hidden. */}
      {remitConflict && (
        <Message
          severity="warn"
          data-testid="benefitVerdict.remitConflict"
          text={`The remit paid at ${formatComparisonValue(
            remitValue ?? null,
            benefit
          )}; the verdict says ${formatComparisonValue(
            verdict?.value ?? null,
            benefit
          )}. A remit outranks a verdict, so the remit is what the engine is scored against — but remits can be wrong too, so both are kept and neither is hidden.`}
        />
      )}

      {showForm && (
        <>
          <div
            role="radiogroup"
            aria-label="What the estimate should have quoted"
            className="flex flex-column gap-2"
            data-testid="benefitVerdict.options"
          >
            {options.map((option, index) => {
              const sources = verdictOptionSources(option);
              return (
                <label
                  key={`${option.value}-${sources.join("")}`}
                  className="flex align-items-center gap-2 cursor-pointer"
                  data-testid={`benefitVerdict.option.${sources.join("-")}`}
                >
                  <RadioButton
                    name={`verdict-${pverifyResponseId}-${benefit}`}
                    checked={
                      choice?.kind === "OPTION" && choice.index === index
                    }
                    onChange={() => setChoice({ kind: "OPTION", index })}
                  />
                  <span className="font-semibold">
                    {verdictOptionValueLabel(option, benefit)}
                  </span>
                  {/* 🚨 Tagged, and in the response table's colours: same source, same look. */}
                  {sources.map((source) => (
                    <Tag
                      key={source}
                      {...VERDICT_SOURCE_TAGS[source]}
                      value={VERDICT_SOURCE_LABELS[source]}
                    />
                  ))}
                  {isChosen(option) && (
                    <span className="text-color-secondary">· recorded</span>
                  )}
                </label>
              );
            })}
            {options.length === 0 && (
              <span className="text-color-secondary">
                No side picked a value for this check.
              </span>
            )}

            <label
              className="flex align-items-center gap-2 cursor-pointer"
              data-testid="benefitVerdict.otherOption"
            >
              <RadioButton
                name={`verdict-${pverifyResponseId}-${benefit}`}
                checked={choice?.kind === "OTHER"}
                onChange={() => setChoice({ kind: "OTHER" })}
              />
              <span className="font-semibold">{ANOTHER_VALUE_LABEL}</span>
              <InputNumber
                value={other}
                onValueChange={(event) => {
                  setOther(event.value ?? null);
                  setChoice({ kind: "OTHER" });
                }}
                placeholder={
                  benefit === "COINSURANCE_PERCENT" ? "e.g. 15%" : "e.g. $150"
                }
                mode={
                  benefit === "COINSURANCE_PERCENT" ? "decimal" : "currency"
                }
                currency="USD"
                locale="en-US"
                suffix={benefit === "COINSURANCE_PERCENT" ? "%" : undefined}
                inputClassName="w-8rem"
                data-testid="benefitVerdict.other"
              />
            </label>

            {/* 🚨 A real answer, in the same group as the values — not an escape hatch beside them. */}
            <label
              className="flex align-items-center gap-2 cursor-pointer"
              data-testid="benefitVerdict.cannotDetermine"
            >
              <RadioButton
                name={`verdict-${pverifyResponseId}-${benefit}`}
                checked={choice?.kind === "CANNOT_DETERMINE"}
                onChange={() => setChoice({ kind: "CANNOT_DETERMINE" })}
              />
              <span className="font-semibold">{CANNOT_DETERMINE_LABEL}</span>
            </label>
          </div>

          <InputText
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Why (optional)"
            className="w-full"
            data-testid="benefitVerdict.note"
          />

          {/* 🚨 What recording will do, said BEFORE anything is written. */}
          <span
            className="text-sm text-color-secondary"
            data-testid="benefitVerdict.consequence"
          >
            {verdictConsequence(chosenLabel, Boolean(verdict))}
          </span>

          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              className="p-button-text p-button-sm p-button-secondary"
              data-testid="benefitVerdict.cancel"
              onClick={reset}
            />
            <Button
              type="button"
              label={RECORD_VERDICT_LABEL}
              className="p-button-sm"
              disabled={
                choice === null ||
                isLoading ||
                (choice.kind === "OTHER" && other === null)
              }
              data-testid="benefitVerdict.record"
              onClick={submit}
            />
          </div>
        </>
      )}

      {isError && (
        <Message severity="error" text="That verdict could not be saved." />
      )}
    </div>
  );
}
